import * as React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
    Box, Button, Grid, Stack, TextField, Typography, Divider, Snackbar, Alert, Card, CardContent, IconButton, CircularProgress
} from "@mui/material";
import type { ArticleSection, ArticleFigure } from "../../../types";
import { createArticle, updateArticleSections, setArticlePublish, uploadArticleImage, uploadArticleCoverImage, getArticleById } from "../../../Services/ApiCalls";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadIcon from "@mui/icons-material/Upload";
import ArrowUpward from "@mui/icons-material/ArrowUpward";
import ArrowDownward from "@mui/icons-material/ArrowDownward";
import CloseIcon from "@mui/icons-material/Close";
import ImageIcon from "@mui/icons-material/Image";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";

type Toast = { open: boolean; severity: "success" | "error" | "info"; msg: string };

export default function AdminArticleEditor() {
    const navigate = useNavigate();
    const params = useParams<{ id?: string }>();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { state } = useLocation() as { state?: { slug?: string } };
    const token = useSelector((state: RootState) => state.auth.token);

    const [articleId, setArticleId] = React.useState<number | null>(params.id ? Number(params.id) : null);

    // Step 1: meta
    const [title, setTitle] = React.useState("");
    const [excerpt, setExcerpt] = React.useState("");
    const [coverImage, setCoverImage] = React.useState("");

    // Step 2: sections
    const [sections, setSections] = React.useState<ArticleSection[]>([]);

    const [saving, setSaving] = React.useState(false);
    const [toast, setToast] = React.useState<Toast>({ open: false, severity: "success", msg: "" });
    const [loading, setLoading] = React.useState(false);
    const [uploadingCover, setUploadingCover] = React.useState(false);

    // Load existing article data when editing
    const loadArticleData = async () => {
        if (!articleId || !token) return;
        
        try {
            setLoading(true);
            const data = await getArticleById(articleId);
            const article = data.article;
            
            setTitle(article.title || '');
            setExcerpt(article.excerpt || '');
            setCoverImage(article.coverImage || '');
            
            // Load sections if they exist, sorted by order
            if (article.sections && Array.isArray(article.sections)) {
                // Deduplicate sections by ID (in case backend returns duplicates)
                const seen = new Map();
                const uniqueSections = [];
                for (const section of article.sections) {
                    if (section.id && !seen.has(section.id)) {
                        seen.set(section.id, true);
                        uniqueSections.push(section);
                    } else if (!section.id) {
                        // If no ID, check by order + title to avoid duplicates
                        const key = `${section.order}-${section.title}`;
                        if (!seen.has(key)) {
                            seen.set(key, true);
                            uniqueSections.push(section);
                        }
                    }
                }
                
                // Deduplicate figures within each section and ensure IDs are preserved
                const sectionsWithDedupedFigures = uniqueSections.map(section => {
                    if (!section.figures || !Array.isArray(section.figures)) {
                        return { ...section, figures: [] };
                    }
                    
                    // Deduplicate figures by ID, or by order+imageUrl if no ID
                    const seenFigures = new Map();
                    const uniqueFigures = [];
                    for (const figure of section.figures) {
                        // Ensure all figure properties are preserved
                        const cleanFigure = {
                            id: figure.id || undefined,
                            sectionId: figure.sectionId || undefined,
                            order: figure.order || 0,
                            imageUrl: figure.imageUrl || '',
                            caption: figure.caption || '',
                            altText: figure.altText || ''
                        };
                        
                        if (cleanFigure.id && !seenFigures.has(cleanFigure.id)) {
                            seenFigures.set(cleanFigure.id, true);
                            uniqueFigures.push(cleanFigure);
                        } else if (!cleanFigure.id) {
                            // If no ID, check by order + imageUrl to avoid duplicates
                            const key = `${cleanFigure.order}-${cleanFigure.imageUrl}`;
                            if (!seenFigures.has(key)) {
                                seenFigures.set(key, true);
                                uniqueFigures.push(cleanFigure);
                            }
                        }
                    }
                    // Sort figures by order to ensure consistent display
                    uniqueFigures.sort((a, b) => (a.order || 0) - (b.order || 0));
                    return { ...section, figures: uniqueFigures };
                });
                
                const sortedSections = sectionsWithDedupedFigures.sort((a, b) => (a.order || 0) - (b.order || 0));
                setSections(sortedSections);
            } else {
                setSections([]);
            }
        } catch (error) {
            console.error('Error loading article:', error);
            setToast({ 
                open: true, 
                severity: "error", 
                msg: error instanceof Error ? error.message : "Failed to load article" 
            });
        } finally {
            setLoading(false);
        }
    };

    // Load data when component mounts or articleId changes
    React.useEffect(() => {
        loadArticleData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [articleId, token]);

    const addSection = () => {
        const order = sections.length + 1;
        setSections([...sections, { order, title: `Section ${order}`, bodyMarkdown: "", figures: [] }]);
    };
    const removeSection = (idx: number) => {
        const next = sections.filter((_, i) => i !== idx).map((s, i) => ({ ...s, order: i + 1 }));
        setSections(next);
    };
    const moveSection = (idx: number, dir: -1 | 1) => {
        const j = idx + dir;
        if (j < 0 || j >= sections.length) return;
        const copy = [...sections];
        [copy[idx], copy[j]] = [copy[j], copy[idx]];
        setSections(copy.map((s, i) => ({ ...s, order: i + 1 })));
    };

    const updateSection = (idx: number, patch: Partial<ArticleSection>) => {
        const copy = [...sections];
        copy[idx] = { ...copy[idx], ...patch };
        setSections(copy);
    };

    const addFigure = (sIdx: number) => {
        const figs = sections[sIdx].figures || [];
        const next: ArticleFigure = { order: figs.length + 1, imageUrl: "", caption: "", altText: "" };
        updateSection(sIdx, { figures: [...figs, next] });
    };

    const removeFigure = (sIdx: number, fIdx: number) => {
        const figs = (sections[sIdx].figures || []).filter((_, i) => i !== fIdx).map((f, i) => ({ 
            ...f, 
            order: i + 1,
            // Preserve ID if it exists
            id: f.id 
        }));
        updateSection(sIdx, { figures: figs });
    };

    const moveFigure = (sIdx: number, fIdx: number, dir: -1 | 1) => {
        const figs = [...(sections[sIdx].figures || [])];
        const j = fIdx + dir;
        if (j < 0 || j >= figs.length) return;
        [figs[fIdx], figs[j]] = [figs[j], figs[fIdx]];
        // Preserve all properties including IDs when reordering
        updateSection(sIdx, { figures: figs.map((f, i) => ({ 
            ...f, 
            order: i + 1,
            // Explicitly preserve ID
            id: f.id 
        })) });
    };

    const uploadCoverImageFile = async (file: File) => {
        try {
            setUploadingCover(true);
            const url = await uploadArticleCoverImage(file);
            setCoverImage(url);
            setToast({ open: true, severity: "success", msg: "Cover image uploaded successfully!" });
        } catch (e: any) {
            setToast({ open: true, severity: "error", msg: e?.message || "Cover image upload failed" });
        } finally {
            setUploadingCover(false);
        }
    };

    const clearCoverImage = () => {
        setCoverImage("");
    };

    const uploadFigureFile = async (sIdx: number, fIdx: number, file: File) => {
        try {
            const url = await uploadArticleImage(file); // returns publicUrl
            const figs = [...(sections[sIdx].figures || [])];
            // Preserve all existing properties including ID when updating imageUrl
            figs[fIdx] = { 
                ...figs[fIdx], 
                imageUrl: url,
                // Explicitly preserve ID
                id: figs[fIdx].id 
            };
            updateSection(sIdx, { figures: figs });
            setToast({ open: true, severity: "success", msg: "Image uploaded" });
        } catch (e: any) {
            setToast({ open: true, severity: "error", msg: e?.message || "Upload failed" });
        }
    };

    // NEW: auto-create draft if needed
    const ensureDraft = async (): Promise<number> => {
        if (articleId) return articleId;
        if (!title.trim()) {
            setToast({ open: true, severity: "error", msg: "Please enter a Title to create the draft." });
            throw new Error("missing-title");
        }
        
        if (!token) {
            setToast({ open: true, severity: "error", msg: "Please log in first." });
            throw new Error("no-token");
        }
        
        setSaving(true);
        try {
            const res = await createArticle(title.trim(), excerpt.trim(), coverImage.trim() || undefined, undefined);
            setArticleId(res.article.id);
            setToast({ open: true, severity: "success", msg: "Draft created. You can add sections now." });
            return res.article.id;
        } catch (error) {
            throw error;
        } finally {
            setSaving(false);
        }
    };

    const handleCreate = async () => {
        try {
            await ensureDraft();
        } catch (e: any) {
            if (e?.message !== "missing-title") {
                setToast({ open: true, severity: "error", msg: e?.message || "Create failed" });
            }
        }
    };

    const handleSaveSections = async () => {
        try {
            const id = await ensureDraft(); // auto-create if needed
            setSaving(true);
            
            // Don't save if no sections
            if (!sections || sections.length === 0) {
                console.log('No sections to save, sections:', sections);
                setToast({ open: true, severity: "info", msg: "No sections to save." });
                return;
            }
            
            console.log('Saving sections:', sections);
            
            await updateArticleSections(id, sections);
            setToast({ open: true, severity: "success", msg: "Sections saved." });
        } catch (e: any) {
            console.error('Error saving sections:', e);
            if (e?.message !== "missing-title") {
                setToast({ open: true, severity: "error", msg: e?.message || "Save failed" });
            }
        } finally {
            setSaving(false);
        }
    };

    const handlePublish = async () => {
        try {
            const id = await ensureDraft(); // auto-create if needed
            setSaving(true);
            await setArticlePublish(id, true);
            setToast({ open: true, severity: "success", msg: "Article published." });
            navigate("/admin/articles");
        } catch (e: any) {
            if (e?.message !== "missing-title") {
                setToast({ open: true, severity: "error", msg: e?.message || "Publish failed" });
            }
        } finally {
            setSaving(false);
        }
    };

    if (loading && articleId) {
        return (
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <CircularProgress />
                <Typography>Loading article data...</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>
                {articleId ? `Edit Article #${articleId}` : "Create New Article"}
            </Typography>
            
            {/* Debug: Show auth status */}
            {!token && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    You are not logged in. Please log in first.
                </Alert>
            )}

            {/* Step 1: Meta */}
            <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="subtitle1" sx={{ mb: 2 }}>1) Article Meta</Typography>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField label="Title" fullWidth value={title} onChange={(e) => setTitle(e.target.value)} />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField label="Excerpt" fullWidth multiline rows={3} value={excerpt} onChange={(e) => setExcerpt(e.target.value)} />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>Cover Image</Typography>
                            <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems="flex-start">
                                {/* Image Preview */}
                                <Box 
                                    sx={{ 
                                        width: { xs: "100%", md: 300 }, 
                                        height: 200, 
                                        bgcolor: "action.hover", 
                                        borderRadius: 2, 
                                        overflow: "hidden", 
                                        flexShrink: 0,
                                        position: "relative",
                                        border: "2px dashed",
                                        borderColor: coverImage ? "transparent" : "divider",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center"
                                    }}
                                >
                                    {uploadingCover ? (
                                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                                            <CircularProgress />
                                            <Typography variant="caption" color="text.secondary">Uploading...</Typography>
                                        </Box>
                                    ) : coverImage ? (
                                        <>
                                            <img 
                                                src={coverImage} 
                                                alt="Cover preview" 
                                                style={{ 
                                                    width: "100%", 
                                                    height: "100%", 
                                                    objectFit: "cover" 
                                                }} 
                                            />
                                            <IconButton
                                                onClick={clearCoverImage}
                                                sx={{
                                                    position: "absolute",
                                                    top: 8,
                                                    right: 8,
                                                    bgcolor: "rgba(0, 0, 0, 0.5)",
                                                    color: "white",
                                                    "&:hover": { bgcolor: "rgba(0, 0, 0, 0.7)" }
                                                }}
                                                size="small"
                                            >
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        </>
                                    ) : (
                                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                                            <ImageIcon sx={{ fontSize: 48, color: "text.secondary" }} />
                                            <Typography variant="caption" color="text.secondary" textAlign="center">
                                                No cover image
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                                
                                {/* Upload Controls */}
                                <Box sx={{ flex: 1, width: { xs: "100%", md: "auto" } }}>
                                    <Stack spacing={2}>
                                        <Button
                                            component="label"
                                            variant="contained"
                                            startIcon={<UploadIcon />}
                                            disabled={uploadingCover || !token}
                                            sx={{ width: { xs: "100%", md: "auto" } }}
                                        >
                                            {uploadingCover ? "Uploading..." : "Upload Cover Image"}
                                            <input
                                                hidden
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) => {
                                                    const file = e.target.files?.[0];
                                                    if (file) {
                                                        // Validate file size (10MB)
                                                        if (file.size > 10 * 1024 * 1024) {
                                                            setToast({ 
                                                                open: true, 
                                                                severity: "error", 
                                                                msg: "File size must be less than 10MB" 
                                                            });
                                                            return;
                                                        }
                                                        // Validate file type
                                                        if (!file.type.startsWith('image/')) {
                                                            setToast({ 
                                                                open: true, 
                                                                severity: "error", 
                                                                msg: "Please select an image file" 
                                                            });
                                                            return;
                                                        }
                                                        uploadCoverImageFile(file);
                                                    }
                                                    // Reset input so same file can be selected again
                                                    e.target.value = '';
                                                }}
                                            />
                                        </Button>
                                        
                                        <TextField 
                                            label="Or enter Cover Image URL" 
                                            fullWidth 
                                            size="small"
                                            value={coverImage} 
                                            onChange={(e) => setCoverImage(e.target.value)}
                                            placeholder="https://example.com/image.jpg"
                                            helperText="Upload an image or paste a URL"
                                        />
                                        
                                        {coverImage && (
                                            <Button
                                                variant="outlined"
                                                color="error"
                                                startIcon={<CloseIcon />}
                                                onClick={clearCoverImage}
                                                size="small"
                                            >
                                                Clear Image
                                            </Button>
                                        )}
                                    </Stack>
                                </Box>
                            </Stack>
                        </Grid>
                    </Grid>

                    {!articleId && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            Create a draft (enter a Title and click “Create Draft”) to enable saving and publishing.
                            (Images can be selected anytime.)
                        </Alert>
                    )}

                    <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                        <Button variant="contained" onClick={handleCreate} disabled={saving || !title.trim()}>
                            {articleId ? "Draft Created" : "Create Draft"}
                        </Button>
                    </Stack>
                </CardContent>
            </Card>

            {/* Step 2: Sections */}
            <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="subtitle1" sx={{ mb: 2 }}>2) Sections & Figures</Typography>
                    <Stack spacing={2}>
                        {sections.map((s, sIdx) => (
                            <Box key={sIdx} sx={{ border: "1px dashed", borderColor: "divider", p: 2, borderRadius: 2 }}>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                    <Typography variant="subtitle2">Section {s.order}</Typography>
                                    <IconButton size="small" onClick={() => moveSection(sIdx, -1)}><ArrowUpward fontSize="small" /></IconButton>
                                    <IconButton size="small" onClick={() => moveSection(sIdx, 1)}><ArrowDownward fontSize="small" /></IconButton>
                                    <IconButton size="small" onClick={() => removeSection(sIdx)}><DeleteIcon fontSize="small" /></IconButton>
                                </Stack>

                                <Grid container spacing={2}>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <TextField
                                            label="Section Title"
                                            fullWidth
                                            value={s.title}
                                            onChange={(e) => updateSection(sIdx, { title: e.target.value })}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12 }}>
                                        <TextField
                                            label="Body (Markdown)"
                                            fullWidth
                                            multiline
                                            minRows={6}
                                            value={s.bodyMarkdown}
                                            onChange={(e) => updateSection(sIdx, { bodyMarkdown: e.target.value })}
                                        />
                                    </Grid>
                                </Grid>

                                <Divider sx={{ my: 2 }} />

                                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                                    <Typography variant="subtitle2">Figures</Typography>
                                    <Button onClick={() => addFigure(sIdx)}>Add Figure</Button>
                                </Stack>

                                <Grid container spacing={2}>
                                    {(s.figures || []).map((f, fIdx) => (
                                        <Grid key={`${sIdx}-${fIdx}`} size={{ xs: 12, md: 6 }}>
                                            <Card variant="outlined">
                                                <CardContent>
                                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                                        <Typography variant="body2">Figure {f.order}</Typography>
                                                        <IconButton size="small" onClick={() => moveFigure(sIdx, fIdx, -1)}><ArrowUpward fontSize="small" /></IconButton>
                                                        <IconButton size="small" onClick={() => moveFigure(sIdx, fIdx, 1)}><ArrowDownward fontSize="small" /></IconButton>
                                                        <IconButton size="small" onClick={() => removeFigure(sIdx, fIdx)}><DeleteIcon fontSize="small" /></IconButton>
                                                    </Stack>

                                                    <Stack direction="row" spacing={2}>
                                                        <Box sx={{ width: 160, height: 120, bgcolor: "action.hover", borderRadius: 1, overflow: "hidden" }}>
                                                            {f.imageUrl ? (
                                                                <img src={f.imageUrl} alt={f.altText || ""} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                                            ) : (
                                                                <Box sx={{ width: "100%", height: "100%", display: "grid", placeItems: "center" }}>
                                                                    <Typography variant="caption" color="text.secondary">No image</Typography>
                                                                </Box>
                                                            )}
                                                        </Box>

                                                        <Box sx={{ flex: 1 }}>
                                                            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                                                                {/* ENABLE image selection even without articleId */}
                                                                <Button
                                                                    component="label"
                                                                    variant="outlined"
                                                                    startIcon={<UploadIcon />}
                                                                    disabled={saving}
                                                                >
                                                                    Select Image
                                                                    <input
                                                                        hidden
                                                                        type="file"
                                                                        accept="image/*"
                                                                        onChange={(e) => {
                                                                            const file = e.target.files?.[0];
                                                                            if (file) uploadFigureFile(sIdx, fIdx, file);
                                                                        }}
                                                                    />
                                                                </Button>
                                                            </Stack>

                                                            <TextField
                                                                label="Caption"
                                                                fullWidth
                                                                sx={{ mb: 1 }}
                                                                value={f.caption || ""}
                                                                onChange={(e) => {
                                                                    const figs = [...(sections[sIdx].figures || [])];
                                                                    // Preserve ID when updating caption
                                                                    figs[fIdx] = { 
                                                                        ...figs[fIdx], 
                                                                        caption: e.target.value,
                                                                        id: figs[fIdx].id 
                                                                    };
                                                                    updateSection(sIdx, { figures: figs });
                                                                }}
                                                            />
                                                            <TextField
                                                                label="Alt text"
                                                                fullWidth
                                                                value={f.altText || ""}
                                                                onChange={(e) => {
                                                                    const figs = [...(sections[sIdx].figures || [])];
                                                                    // Preserve ID when updating altText
                                                                    figs[fIdx] = { 
                                                                        ...figs[fIdx], 
                                                                        altText: e.target.value,
                                                                        id: figs[fIdx].id 
                                                                    };
                                                                    updateSection(sIdx, { figures: figs });
                                                                }}
                                                            />
                                                        </Box>
                                                    </Stack>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>
                        ))}
                    </Stack>

                    <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                        <Button variant="outlined" onClick={addSection}>Add Section</Button>
                        <Button variant="contained" onClick={handleSaveSections} disabled={saving}>
                            Save Sections
                        </Button>
                    </Stack>
                </CardContent>
            </Card>

            {/* Step 3: Publish */}
            <Card variant="outlined">
                <CardContent>
                    <Typography variant="subtitle1" sx={{ mb: 2 }}>3) Publish</Typography>
                    <Stack direction="row" spacing={2}>
                        <Button variant="contained" color="success" onClick={handlePublish} disabled={saving}>
                            Publish Article
                        </Button>
                        <Button variant="outlined" onClick={() => navigate("/admin/articles")}>Back to list</Button>
                    </Stack>
                </CardContent>
            </Card>

            <Snackbar open={toast.open} autoHideDuration={3000} onClose={() => setToast((t) => ({ ...t, open: false }))}>
                <Alert severity={toast.severity} onClose={() => setToast((t) => ({ ...t, open: false }))}>
                    {toast.msg}
                </Alert>
            </Snackbar>
        </Box>
    );
}